import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import SitePage, { CallToAction, styles, T, ACCENT } from '../components/site/SitePage';
import { blogPosts } from '../data/blogPosts';
import NotFoundPage from './NotFoundPage';

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.id === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Même page que 404.html, que Cloudflare sert pour un article inconnu : rien à recalculer au chargement.
  if (!post) return <NotFoundPage />;

  const otherPosts = blogPosts.filter(p => p.id !== post.id);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.ogImage || post.image,
    "datePublished": post.date,
    "dateModified": post.updated || post.date,
    "author": {
      "@type": "Person",
      "name": "Mattéo",
      "url": "https://autoria.dev/about"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Autoria",
      "url": "https://autoria.dev",
      "logo": {
        "@type": "ImageObject",
        "url": "https://autoria.dev/favicon.svg"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://autoria.dev/blog/${post.id}`
    },
    "keywords": post.tags.join(", "),
    "articleSection": post.category,
    "inLanguage": "fr-FR"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://autoria.dev/" },
      { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://autoria.dev/blog" },
      { "@type": "ListItem", "position": 3, "name": post.title, "item": `https://autoria.dev/blog/${post.id}` }
    ]
  };

  const renderMarkdown = (content) => {
    return content
      .split('\n\n')
      .map((block, i) => {
        if (block.startsWith('## ')) {
          return <h2 key={i} style={{ ...styles.h2, fontSize: 28, margin: '40px 0 12px' }}>{block.replace('## ', '')}</h2>;
        }
        if (block.startsWith('### ')) {
          return <h3 key={i} style={{ margin: '28px 0 8px', fontSize: 20, fontWeight: 800, fontStretch: '85%', color: ACCENT }}>{block.replace('### ', '')}</h3>;
        }
        if (block.startsWith('| ')) {
          const rows = block.split('\n').filter(r => !r.match(/^\|\s*[-]+/));
          const headers = rows[0]?.split('|').filter(Boolean).map(h => h.trim());
          const dataRows = rows.slice(1).map(r => r.split('|').filter(Boolean).map(c => c.trim()));
          return (
            <div key={i} style={{ overflowX: 'auto', margin: '24px 0', border: `1px solid ${T.line}`, background: '#FFFFFF' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
                <thead>
                  <tr style={{ background: '#F1F2F4' }}>
                    {headers?.map((h, j) => (
                      <th key={j} style={{ padding: '10px 14px', textAlign: 'left', fontFamily: T.mono, fontSize: 13, fontWeight: 600, color: T.muted }}>{renderInline(h)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dataRows.map((row, j) => (
                    <tr key={j} style={{ borderTop: `1px solid ${T.line}` }}>
                      {row.map((cell, k) => (
                        <td key={k} style={{ padding: '10px 14px', color: T.text }}>{renderInline(cell)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (block.startsWith('1. ') || block.startsWith('- ')) {
          const isOrdered = block.startsWith('1. ');
          const items = block.split('\n').map(item => item.replace(/^(\d+\.\s|-\s)/, ''));
          const ListTag = isOrdered ? 'ol' : 'ul';
          return (
            <ListTag key={i} style={{ margin: '16px 0', paddingLeft: 22, listStyle: isOrdered ? 'decimal' : 'disc', color: T.text, lineHeight: 1.6, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {items.map((item, j) => <li key={j}>{renderInline(item)}</li>)}
            </ListTag>
          );
        }
        return <p key={i} style={{ margin: '16px 0', color: T.text, lineHeight: 1.65 }}>{renderInline(block)}</p>;
      });
  };

  const renderInline = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*|\[([^\]]+)\]\(([^)]+)\))/g);
    return parts.map((part, i) => {
      if (!part) return null;
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} style={{ color: T.ink, fontWeight: 700 }}>{part.slice(2, -2)}</strong>;
      }
      const linkMatch = part.match(/\[([^\]]+)\]\(([^)]+)\)/);
      if (linkMatch) {
        const [, linkText, href] = linkMatch;
        if (href.startsWith('/') || href.startsWith('#')) {
          return <Link key={i} to={href} style={{ color: ACCENT }}>{linkText}</Link>;
        }
        return <a key={i} href={href} style={{ color: ACCENT }} target="_blank" rel="noopener noreferrer">{linkText}</a>;
      }
      return part;
    });
  };

  const date = new Date(post.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  const updated = post.updated && new Date(post.updated).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <SitePage>
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.id}`}
        image={post.ogImage || post.image}
        type="article"
        article={{ publishedTime: post.date, tags: post.tags }}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <article style={{ ...styles.narrow, paddingTop: 48 }}>
        <Link to="/blog" style={{ fontFamily: T.mono, fontSize: 13, color: T.faint, textDecoration: 'none' }}>← Retour au blog</Link>
        <p style={{ ...styles.kicker, marginTop: 28 }}>{post.category} · {date}{updated ? ` · mis à jour le ${updated}` : ''} · {post.readTime} de lecture</p>
        <h1 style={{ ...styles.h1, fontSize: 'clamp(32px, 4vw, 48px)', marginTop: 12 }}>{post.title}</h1>
        <img src={post.image} alt="" width="800" height="450" style={{ height: 'auto',  display: 'block', width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', margin: '32px 0 8px', borderRadius: 8, background: T.line }} />
        <div>{renderMarkdown(post.content)}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 32, paddingTop: 20, borderTop: `1px solid ${T.line}` }}>
          {post.tags.map(tag => (
            <span key={tag} style={{ padding: '3px 10px', border: `1px solid ${T.line2}`, borderRadius: 3, fontFamily: T.mono, fontSize: 12, color: T.muted }}>{tag}</span>
          ))}
        </div>
      </article>

      {otherPosts.length > 0 && (
        <section style={{ ...styles.narrow, marginTop: 56 }}>
          <h2 style={{ ...styles.h2, fontSize: 24, marginBottom: 16 }}>Autres articles</h2>
          <div style={{ display: 'flex', flexDirection: 'column', borderTop: `1px solid ${T.line2}` }}>
            {otherPosts.slice(0, 5).map(other => (
              <Link key={other.id} to={`/blog/${other.id}`} className="rowbtn" style={{ padding: '14px 4px', borderBottom: `1px solid ${T.line2}`, color: T.ink, textDecoration: 'none', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontWeight: 700 }}>{other.title}</span>
                <span style={{ fontSize: 14, color: T.muted }}>{other.excerpt}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <CallToAction title="Vos équipes vivent encore dans des Excel ?" text="Le même principe produit vos bons de commande, vos fiches produit ou vos inventaires. 30 minutes pour voir ce qui s'automatise chez vous." from="article" />
    </SitePage>
  );
};

export default BlogPostPage;
